/**
 * TASK-ATTR-019: Effect Blend Mode Selection
 *
 * Maps damage types to PixiJS blend modes for visual appeal.
 * Fire/lightning use additive blending for glowing effects.
 * Ice/water use screen blending for ethereal effects.
 */

/**
 * Get the appropriate blend mode for a given damage type
 *
 * @param damageType - The damage type from the combat attribute
 * @returns Blend mode string for PixiJS
 */
export function getBlendModeForDamageType(damageType: string): string {
  const normalizedType = damageType.toLowerCase();

  switch (normalizedType) {
    // Glowing/energy effects - additive blending
    case 'fire':
    case 'lightning':
    case 'holy':
    case 'light':
    case 'energy':
    case 'arcane':
      return 'add';

    // Ethereal/cool effects - screen blending
    case 'ice':
    case 'water':
    case 'frost':
    case 'shadow':
    case 'dark':
      return 'screen';

    // Soft glow effects - soft-light blending
    case 'nature':
    case 'healing':
    case 'life':
    case 'earth':
      return 'soft-light';

    // Physical/normal effects - normal blending
    case 'physical':
    case 'slash':
    case 'pierce':
    case 'blunt':
    default:
      return 'normal';
  }
}

/**
 * Get effect scale multiplier based on damage type
 * Some effects look better larger or smaller
 *
 * @param damageType - The damage type from the combat attribute
 * @returns Scale multiplier (1.0 = normal size)
 */
export function getScaleForDamageType(damageType: string): number {
  const normalizedType = damageType.toLowerCase();

  switch (normalizedType) {
    // Large explosive effects
    case 'fire':
    case 'lightning':
    case 'arcane':
      return 1.2;

    // Medium area effects
    case 'ice':
    case 'nature':
    case 'holy':
      return 1.0;

    // Smaller focused effects
    case 'shadow':
    case 'dark':
    case 'physical':
    default:
      return 0.9;
  }
}

/**
 * Get effect alpha (transparency) based on damage type
 *
 * @param damageType - The damage type from the combat attribute
 * @returns Alpha value (0-1)
 */
export function getAlphaForDamageType(damageType: string): number {
  const normalizedType = damageType.toLowerCase();

  switch (normalizedType) {
    // Semi-transparent ethereal effects
    case 'shadow':
    case 'dark':
    case 'ghost':
      return 0.8;

    // Full opacity for most effects
    default:
      return 1.0;
  }
}
